<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { disconnectPhone, enrollPhone, phoneCanEnroll, phoneWorkerCommand, retryPhoneCleanup } from './phoneEnrollment.js';
  import { recoverPhoneTest, preparePhoneTest, validatePhoneTest, phoneTestDescription } from './phoneDiagnostics.js';
  import { registerUpdateGuard } from './appUpdates.js';
  import { enrollmentErrorCode } from './phoneEnrollmentErrors.js';

  export let api = null;
  export let ownerId = null;
  const dispatch = createEventDispatcher();
  let alive = false, busy = false, attempted = false, blocked = false;
  let snapshot = null, notifyInbound = false, preview = 'generic', error = '', success = '';
  let phoneConfig = null, phoneBusy = false, phoneBinding = null, phoneNotice = '', phoneError = '', phoneEpoch = 0;
  let credential = null;
  let testRecord = null, testResult = null, testBusy = false, testError = '';
  $: changed = snapshot && (snapshot.enabled !== notifyInbound || snapshot.preview !== preview);
  $: testPending = testRecord && (!testResult || ['pending', 'leased'].includes(testResult.state));

  function validPreferences(value) {
    return value && typeof value.enabled === 'boolean' && ['snippet', 'generic'].includes(value.preview);
  }

  function denied(problem) {
    snapshot = null;
    phoneConfig = null;
    error = 'Patikrinkite prisijungimą ir iš naujo atverkite pašto dėžutę.';
    dispatch('denied', problem);
  }

  async function loadPreferences() {
    if (busy || !api) return;
    const client = api, owner = ownerId;
    busy = true; attempted = true; blocked = true; error = ''; success = ''; snapshot = null; phoneConfig = null;
    try {
      const result = await client('push/');
      if (!alive || client !== api || owner !== ownerId) return;
      if (result.user_id !== owner) { denied({ status: 401 }); return; }
      if (!validPreferences(result.notifications)) throw new Error('Invalid notification preferences');
      phoneConfig = result;
      credential = localStorage.getItem('jwtToken');
      snapshot = { ...result.notifications };
      notifyInbound = snapshot.enabled; preview = snapshot.preview; blocked = false;
      testRecord = recoverPhoneTest(owner); testResult = null; testError = '';
      // Opening settings may recover an existing device, but never enrolls it
      // or sends a diagnostic without a separate explicit click.
      try {
        const state = await phoneWorkerCommand('state');
        if (state.binding?.owner_id === owner) {
          const verified = await client(`push/subscriptions/${state.binding.id}/`, { timeoutMs: 10000 });
          if (alive && client === api && owner === ownerId && credential === localStorage.getItem('jwtToken') &&
              verified.id === state.binding.id && verified.generation === state.binding.generation &&
              verified.owner_id === owner && verified.revoked === false) phoneBinding = verified;
        }
      } catch { /* Settings remain useful when this phone is not enrolled. */ }
    } catch (problem) {
      if (!alive || client !== api || owner !== ownerId || problem.name === 'AbortError') return;
      if ([401, 403].includes(problem.status)) denied(problem);
      else error = 'Nepavyko įkelti nuostatų. Bandykite dar kartą, kai ryšys veiks.';
    } finally { if (alive && client === api && owner === ownerId) busy = false; }
  }

  async function savePreferences() {
    if (busy || blocked || !snapshot || !changed) return;
    const client = api, owner = ownerId;
    busy = true; error = ''; success = '';
    try {
      const result = await client('push/preferences/', { method: 'PATCH',
        body: { expected: snapshot, enabled: notifyInbound, preview } });
      if (!alive || client !== api || owner !== ownerId) return;
      if (!validPreferences(result)) throw new Error('Invalid notification preferences');
      snapshot = { ...result }; notifyInbound = result.enabled; preview = result.preview;
      success = 'Nuostatos išsaugotos. Šio telefono registracija ir pristatymas tikrinami atskirai.';
    } catch (problem) {
      if (!alive || client !== api || owner !== ownerId || problem.name === 'AbortError') return;
      blocked = true;
      if ([401, 403].includes(problem.status)) denied(problem);
      else error = problem.code === 'push_preferences_conflict'
        ? 'Nuostatos pasikeitė kitame lange. Įkelkite esamas nuostatas prieš išsaugodami dar kartą.'
        : 'Nežinoma, ar nuostatos išsaugotos. Pirmiausia įkelkite esamas nuostatas; automatiškai nekartosime.';
    } finally { if (alive && client === api && owner === ownerId) busy = false; }
  }

  async function enablePhone() {
    if (phoneBusy) return;
    const client = api, owner = ownerId, epoch = ++phoneEpoch;
    phoneBusy = true; phoneError = ''; phoneNotice = '';
    try {
      phoneBinding = await enrollPhone({ api: client, config: phoneConfig, ownerId: owner,
        current: () => alive && epoch === phoneEpoch && client === api && owner === ownerId });
      if (alive && epoch === phoneEpoch) phoneNotice = 'Telefonas užregistruotas. Pranešimų pristatymas dar turi būti patikrintas šiame iPhone.';
    } catch (problem) {
      if (alive && epoch === phoneEpoch) phoneError =
        `Nepavyko patvirtinti telefono registracijos. Diagnostikos kodas: ${enrollmentErrorCode(problem)}. Perduokite šį kodą IT; automatiškai nekartosime.`;
    } finally {
      if (alive && epoch === phoneEpoch) {
        permission = Notification.permission;
        phoneBusy = false;
      }
    }
  }

  async function disablePhone() {
    ++phoneEpoch;
    phoneBusy = true; phoneError = ''; phoneNotice = ''; phoneBinding = null;
    try {
      const complete = await disconnectPhone(ownerId, credential);
      if (alive) phoneNotice = complete ? 'Telefonas atjungtas.' : 'Telefono peržiūros išjungtos. Laukiama serverio patvirtinimo; ryšiui atsikūrus patikrinsime dar kartą.';
    } catch {
      if (alive) phoneError = 'Nepavyko užbaigti telefono atjungimo. Patikrinkite ryšį ir pranešimų leidimus iPhone nustatymuose.';
    } finally { if (alive) phoneBusy = false; }
  }

  async function diagnostic(send) {
    if (testBusy || phoneBusy || !api || (send && (!phoneBinding || testPending || !phoneCanEnroll(phoneConfig, ownerId)))) return;
    const client = api, owner = ownerId, epoch = phoneEpoch, token = localStorage.getItem('jwtToken');
    if (!token || token !== credential) return;
    const current = () => alive && client === api && owner === ownerId && epoch === phoneEpoch &&
      token === localStorage.getItem('jwtToken');
    testBusy = true; testError = '';
    try {
      if (send) {
        testRecord = preparePhoneTest(phoneBinding, owner);
        testResult = null;
      }
      const record = testRecord;
      if (!record) return;
      const result = send
        ? await client(`push/subscriptions/${record.subscription_id}/tests/`, { method: 'POST', timeoutMs: 10000,
          body: { request_key: record.request_key, expected_generation: record.generation } })
        : await client(`push/tests/${record.request_key}/`, { timeoutMs: 10000 });
      if (current()) testResult = validatePhoneTest(result, record);
    } catch (problem) {
      if (current()) {
        // A definitive rejection is safe to retry by another explicit click;
        // transport errors leave the durable id available for status lookup.
        if ([400, 403, 404, 409, 429, 503].includes(problem.status))
          testResult = { state: 'failed' };
        testError = problem.status === 429
          ? 'Testo limitas pasiektas. Palaukite prieš siųsdami kitą testą.'
          : 'Nepavyko patvirtinti testo rezultato. Patikrinkite būseną; automatiškai nekartosime.';
      }
    } finally { if (alive) testBusy = false; }
  }

  let dedicatedEntry = false, standalone = false, supported = false, permission = 'default';
  onMount(() => {
    alive = true;
    const removeUpdateGuard = registerUpdateGuard(() => !busy && !phoneBusy && !testBusy &&
      (!snapshot || (snapshot.enabled === notifyInbound && snapshot.preview === preview)));
    dedicatedEntry = window.location.pathname === '/studentplayground/inbox/';
    standalone = window.matchMedia('(display-mode: standalone)').matches || navigator.standalone === true;
    supported = window.isSecureContext && 'serviceWorker' in navigator &&
      typeof window.PushManager !== 'undefined' && typeof window.Notification !== 'undefined';
    permission = typeof window.Notification !== 'undefined' ? window.Notification.permission : 'default';
    credential = localStorage.getItem('jwtToken');
    const logout = () => {
      ++phoneEpoch; phoneBinding = null; phoneConfig = null;
      testRecord = null; testResult = null; testError = '';
      void disablePhone();
    };
    const storage = event => {
      try {
        if (event.storageArea && event.storageArea !== localStorage) return;
        if (localStorage.getItem('jwtToken') !== null) return;
        if ((event.key === 'jwtToken' && event.newValue === null) || event.key === null) logout();
      } catch {}
    };
    const reconnect = () => { void retryPhoneCleanup(ownerId, credential).catch(() => {}); };
    window.addEventListener('tutory:logout', logout);
    window.addEventListener('storage', storage);
    window.addEventListener('online', reconnect);
    return () => {
      alive = false; ++phoneEpoch;
      removeUpdateGuard();
      window.removeEventListener('tutory:logout', logout);
      window.removeEventListener('storage', storage);
      window.removeEventListener('online', reconnect);
    };
  });
</script>

<details class="device-setup">
  <summary>Naudojimas telefone</summary>
  <section aria-label="Telefono paruošimas">
    {#if !dedicatedEntry}
      <p>Telefonui skirta atskira „Tutory žinutės“ piktograma. Mokinių programėlės keisti nereikia.</p>
      <a href="/studentplayground/inbox/#/inbox">Atverti telefono diegimo puslapį</a>
    {:else if !standalone}
      <ol>
        <li>Atverkite šį puslapį „Safari“ ir pasirinkite bendrinimą → „Pridėti prie pradžios ekrano“ (Add to Home Screen).</li>
        <li>Atverkite naują piktogramą „Tutory žinutės“ ir prisijunkite savo asmenine darbuotojo paskyra.</li>
      </ol>
    {:else}
      <p>Atverta iš pradžios ekrano.</p>
    {/if}
    {#if dedicatedEntry && standalone}
      {#if !supported}
        <p>Šiame lange nėra visų pranešimams reikalingų naršyklės funkcijų. Patikrinkite, ar puslapis atvertas iš pradžios ekrano ir naudojamas saugus HTTPS ryšys.</p>
      {:else if permission === 'denied'}
        <p>Leidimas užblokuotas. Peržiūrėkite šios programėlės pranešimų leidimus iPhone nustatymuose. Pakartotinai leidimo neprašysime.</p>
      {:else if permission === 'granted'}
        <p>Naršyklės leidimas suteiktas, bet tai dar nepatvirtina pranešimų pristatymo.</p>
      {:else}
        <p>Naršyklės leidimas pranešimams dar nesuteiktas.</p>
      {/if}
    {/if}
    <p class="pending">Vien piktogramos įdiegimas pranešimų neįjungia. Užregistruokite telefoną ir patikrinkite pristatymą bandomuoju pranešimu. Klientų žinučių gavimas tikrinamas atskirai.</p>
    {#if phoneConfig?.origin_matches === false && phoneConfig?.inbox_url}
      <p role="alert">Šis adresas nesutampa su pranešimų adresu. Atverkite tinkamą adresą „Safari“ ir pridėkite jį prie pradžios ekrano. Nauju adresu gali reikėti prisijungti ir užregistruoti telefoną dar kartą.</p>
      <a href={phoneConfig.inbox_url}>Atverti tinkamą pašto dėžutės adresą</a>
    {/if}
    {#if dedicatedEntry && standalone && supported && phoneConfig?.user_id === ownerId}
      <div class="phone-enrollment">
        {#if phoneCanEnroll(phoneConfig, ownerId)}
          <button type="button" disabled={phoneBusy || permission === 'denied' || !!phoneBinding} on:click={enablePhone}>Įjungti telefono pranešimus</button>
        {/if}
        <button type="button" disabled={phoneBusy} on:click={disablePhone}>Atjungti šį telefoną</button>
      </div>
    {/if}
    {#if phoneNotice}<p role="status" aria-label="Telefono registracijos būsena">{phoneNotice}</p>{/if}
    {#if phoneError}<p role="alert">{phoneError}</p>{/if}
    {#if dedicatedEntry && standalone && supported && phoneConfig?.user_id === ownerId && (phoneBinding || testRecord)}
      <div class="phone-test">
        <button type="button" disabled={phoneBusy || testBusy || !phoneBinding || !!testPending || !phoneCanEnroll(phoneConfig, ownerId)}
          on:click={() => diagnostic(true)}>Siųsti bandomąjį pranešimą</button>
        {#if testRecord}
          <button type="button" disabled={phoneBusy || testBusy} on:click={() => diagnostic(false)}>Tikrinti testo būseną</button>
          <p role="status" aria-label="Bandomojo pranešimo būsena">{phoneTestDescription(testResult)}</p>
        {/if}
        {#if testError}<p role="alert">{testError}</p>{/if}
        <p>Testas skirtas tik šiam telefonui: jis nesukuria žinutės pašto dėžutėje ir nesiunčia WhatsApp ar Viber žinutės. Jis nekeičia klientų pokalbių ar jūsų pranešimų nuostatų.</p>
      </div>
    {/if}
    {#if api && ownerId}
      <div class="preferences">
        <button type="button" disabled={busy} on:click={loadPreferences}>{attempted ? 'Įkelti esamas nuostatas' : 'Pranešimų nuostatos'}</button>
        {#if busy}<p role="status">Tikrinamos nuostatos…</p>{/if}
        {#if error}<p role="alert">{error}</p>{/if}
        {#if success && !changed}<p role="status" aria-label="Pranešimų nuostatų būsena">{success}</p>{/if}
        {#if snapshot}
          <form on:submit|preventDefault={savePreferences}>
            <fieldset disabled={busy}>
              <legend>Mano paskyros pranešimai</legend>
              <label class="toggle"><input type="checkbox" bind:checked={notifyInbound} />Gauti naujų žinučių pranešimus</label>
              <label class="preview">Turinys užrakintame ekrane
                <select bind:value={preview}>
                  <option value="generic">Tik bendras pranešimas</option>
                  <option value="snippet">Vardas, kanalas ir žinutės ištrauka</option>
                </select>
              </label>
              <p>Nuostatos galioja visiems jūsų užregistruotiems įrenginiams. Kitų darbuotojų nuostatų jos nekeičia. Telefone leidimą vis tiek reikia suteikti atskirai.</p>
              <button type="submit" disabled={blocked || !changed}>Išsaugoti nuostatas</button>
            </fieldset>
          </form>
        {/if}
      </div>
    {/if}
  </section>
</details>

<style>
  .device-setup { border-bottom: 1px solid #e5e1da; padding: .65rem 1rem; background: #faf9f6; color: #38332e; font-size: .85rem; }
  summary { cursor: pointer; width: fit-content; font-weight: 600; }
  summary:focus-visible, a:focus-visible { outline: 3px solid #c84b09; outline-offset: 4px; }
  section { max-width: 44rem; line-height: 1.6; padding-top: .5rem; }
  p { margin: .45rem 0; }
  ol { margin: .45rem 0; padding-left: 1.4rem; }
  a { color: #9c3800; text-underline-offset: .2em; }
  .pending { color: #635b53; }
  .preferences { margin-top: .75rem; border-top: 1px solid #e5e1da; padding-top: .75rem; }
  fieldset { border: 0; padding: .75rem 0 0; margin: 0; min-width: 0; }
  legend { font-weight: 600; }
  .toggle { display: flex; align-items: center; gap: .5rem; padding: .5rem 0; }
  .preview { display: grid; gap: .25rem; max-width: 24rem; }
  select, button { font: inherit; min-height: 44px; border: 1px solid #bdb4aa; border-radius: .35rem; padding: .5rem .75rem; background: white; color: #38332e; }
  button { cursor: pointer; }
  button:disabled { cursor: default; opacity: .55; }
  input { width: 1.1rem; height: 1.1rem; accent-color: #c84b09; }
  input:focus-visible, select:focus-visible, button:focus-visible { outline: 3px solid #c84b09; outline-offset: 3px; }
  [role="alert"] { color: #982f20; }
</style>
